import { useState, useCallback, useRef, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../config/firebase';
import { preprocessEnglishSentence, shuffleArray } from '../../utils/quizUtils';
import { createLogger } from '../../utils/logger';
import { useQuizCore } from './core';
// （移行完了）useQuizCore を利用した統一フロー

// ログシステムの初期化
const logger = createLogger('QuizPlayer');

const useQuizPlayer = (modalContentId, activeSubject, isModalOpen, studentId, timerControls = {}, contentDetails = {}) => {
  // useQuizCore を初期化（結果確定などの中心ロジックを担当）
  const { finalizeQuizAttempt: coreFinalizeQuizAttempt } = useQuizCore();

  // 状態管理
  const [questions, setQuestions] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sessionResponses, setSessionResponses] = useState({});
  const [freeNavigation, setFreeNavigation] = useState(false);
  const [answerRevealMode, setAnswerRevealMode] = useState('immediate');
  const [showFinalSummary, setShowFinalSummary] = useState(false);
  const [isFinalizingAttempt, setIsFinalizingAttempt] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // refs
  const attemptFinalizedRef = useRef(false);

  // タイマー制御関数の取得（現在は未使用）
  // const { pauseTimer, resumeTimer } = timerControls;

  // ユーティリティ関数
  const scrollToTop = useCallback(() => {
    const modalContent = document.querySelector('.fixed.inset-0.bg-white');
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  }, []);

  // 結果確定処理
  const finalizeAttempt = useCallback(async (forced = false, responses = sessionResponses) => {
    logger.info('finalizeAttempt 開始', { forced });
    
    if (isFinalizingAttempt || attemptFinalizedRef.current) {
      logger.info('重複実行防止により処理停止');
      return;
    }

    setIsFinalizingAttempt(true);
    attemptFinalizedRef.current = true;

    try {
      const total = questions.length;
      const answeredCount = Object.keys(responses).length;
      
      // 全問回答チェック（手動結果表示の場合は実行、時間切れでない場合は全問回答必須）
      if (!forced && answeredCount < total) {
        logger.info('全問未回答のため結果記録停止');
        logger.debug('回答状況:', { answeredCount, total, forced });
        setIsFinalizingAttempt(false);
        attemptFinalizedRef.current = false;
        return {
          success: false,
          message: '全ての問題を解いていないため、結果は記録されませんでした。'
        };
      }

      // タイムオーバーの場合、Firebase保存用のresponsesにも「時間切れ」を反映
      let updatedResponses = { ...responses };
      if (forced) {
        questions.forEach((q) => {
          if (!updatedResponses[q.id]) {
            updatedResponses[q.id] = {
              isCorrect: false,
              explanation: "時間切れです。",
              correctAnswer: q.type === 'sentence-rearrangement' ? q.englishSentence : (q.answer || ""),
              userAnswer: "",
              type: q.type,
              details: q
            };
          }
        });
      }

      // データの検証と適切なフォーマットへの変換
      Object.keys(updatedResponses).forEach(key => {
        const response = updatedResponses[key];
        const question = questions.find(q => q.id === key);
        
        if (question) {
          if (question.type === 'sentence-rearrangement') {
            updatedResponses[key] = {
              ...response,
              correctAnswer: question.englishSentence,
              userAnswer: response.userAnswer || "",
              details: {
                ...question,
                englishSentence: question.englishSentence,
                japaneseTranslation: question.japaneseTranslation
              }
            };
          } else if (question.type === 'fill-in-the-blank') {
            updatedResponses[key] = {
              ...response,
              details: {
                type: question.type,
                question: question.question,
                answer: question.answer,
                explanation: question.explanation || "",
                userResponse: {
                  isCorrect: response.isCorrect,
                  explanation: response.explanation,
                  userAnswer: response.userAnswer,
                  correctAnswer: question.answer
                }
              }
            };
          }
        }
      });

      const currentEndTime = Date.now();

      // 終了時間を記録（結果画面で固定時間表示のため）
      setEndTime(currentEndTime);

      // 詳細情報（UI 解析用）の生成は useQuizCore 側で必要に応じて行うため、ここでは省略

      // --------------------
      // 🔄 useQuizCore による正式保存フロー
      // --------------------

      const coreResult = await coreFinalizeQuizAttempt({
        questions,
        responses: updatedResponses,
        studentId,
        contentId: modalContentId,
        startTime,
        forced,
      });

      if (!coreResult.success) {
        throw new Error(coreResult.message || 'useQuizCore.finalizeQuizAttempt が失敗しました');
      }

      logger.info('結果表示画面に遷移します');
      setShowFinalSummary(true);
      // useQuizCore が返却した情報をそのまま返す
      return coreResult;

    } catch (error) {
      console.error('🔴 useQuizPlayer - 結果の保存に失敗しました:', error);
      console.error('🔴 useQuizPlayer - エラーの詳細:', error.message);
      console.error('🔴 useQuizPlayer - このフックは使用停止のはずです');
      
      // エラー時はフラグをリセット
      setIsFinalizingAttempt(false);
      attemptFinalizedRef.current = false;
      
      return {
        success: false,
        message: '結果の保存に失敗しました。'
      };
    } finally {
      setIsFinalizingAttempt(false);
    }
  }, [questions, modalContentId, studentId, sessionResponses, startTime, coreFinalizeQuizAttempt, isFinalizingAttempt]);

  // 回答提出処理
  const handleQuestionSubmit = useCallback((questionIndex, isCorrect, explanation, correctAnswer, userAnswer) => {
    const question = questions[questionIndex];
    if (!question) return;

    const updatedResponses = {
      ...sessionResponses,
      [question.id]: { 
        isCorrect, 
        explanation, 
        correctAnswer,
        userAnswer,
        type: question.type,
        details: question
      }
    };

    logger.info('回答送信 - タイマー制御はQuizSectionで実行');
    logger.debug('updatedResponses設定前の状態確認:', {
      totalQuestions: questions.length,
      currentAnswers: Object.keys(sessionResponses).length,
      newAnswers: Object.keys(updatedResponses).length,
      questionIndex,
      questionId: question.id
    });

    setSessionResponses(updatedResponses);

    if (answerRevealMode === 'immediate') {
      setShowFeedback(true);
    } else {
      setShowFeedback(false);
    }

    // 更新されたresponses（最新の状態）で全問回答判定を実行
    const allAnswered = questions.every(q => updatedResponses[q.id]);
    logger.debug('回答チェック - 全問回答:', { allAnswered, attemptFinalized: attemptFinalizedRef.current });
    logger.debug('詳細チェック:', {
      totalQuestions: questions.length,
      answeredQuestions: Object.keys(updatedResponses).length,
      unansweredQuestions: questions.filter(q => !updatedResponses[q.id]).map(q => q.id)
    });

    if (allAnswered && !attemptFinalizedRef.current) {
      logger.info('全問回答完了 - 設定ベース制御での処理判定');
      
      // quickAnswerMode設定に基づく自動実行制御
      const quickAnswerMode = contentDetails?.quickAnswerMode || 'standard';
      logger.debug('quickAnswerMode設定:', quickAnswerMode);
      
      if (quickAnswerMode === 'quick') {
        logger.info('クイック回答モード - 自動でfinalizeAttempt実行');
        // 即座にfinalizeAttemptを実行（遅延なし）
        finalizeAttempt(false, updatedResponses).then((result) => {
          logger.info('自動finalizeAttempt完了:', result);
        }).catch((error) => {
          logger.error('自動finalizeAttemptエラー:', error);
        });
      } else {
        logger.info('標準モード - ユーザーの「結果を表示」ボタンクリック待ち');
        // 自動実行せず、ユーザーの操作を待つ
      }
    }

    setTimeout(scrollToTop, 0);
  }, [questions, answerRevealMode, sessionResponses, finalizeAttempt, scrollToTop, contentDetails]);

  // 質問データの取得とセットアップ
  useEffect(() => {
    if (!modalContentId || !isModalOpen) {
      setQuestions([]);
      return;
    }

    const contentRef = ref(database, `contents/${modalContentId}`);
    const unsubscribe = onValue(contentRef, (snapshot) => {
      const content = snapshot.val();
      if (content && content.quizzes) {
        let quizzes = Object.entries(content.quizzes).map(([quizId, quiz]) => ({
          id: quizId,
          contentId: modalContentId,
          contentTitle: content.title,
          ...quiz
        }));

        // quizzes配列の処理
        quizzes = quizzes.map(quiz => {
          if (quiz.type === 'sentence-rearrangement') {
            const processed = preprocessEnglishSentence(quiz.englishSentence || quiz.sentence || '');
            return {
              ...quiz,
              words: processed.words,
              lastPunctuation: processed.lastPunctuation,
              sentence: processed.sentence
            };
          }
          return quiz;
        });

        if (activeSubject !== 'all') {
          quizzes = quizzes.filter((q) => q.subject === activeSubject);
        }

        // タイムスタンプでソート
        quizzes.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

        // ランダム出題の処理
        if (content.randomizeQuizzes) {
          quizzes = shuffleArray([...quizzes]);
        }

        setFreeNavigation(!!content.freeNavigation);
        setAnswerRevealMode(content.answerRevealMode || 'immediate');
        // ------- パフォーマンス向上: 重複更新の回避 -------
        setQuestions((prev) => {
          // 既存と長さが異なる場合は更新
          if (prev.length !== quizzes.length) return quizzes;

          // id と timestamp で簡易比較（全件）
          const isSame = prev.every((q, idx) => q.id === quizzes[idx].id && (q.timestamp || 0) === (quizzes[idx].timestamp || 0));

          return isSame ? prev : quizzes;
        });
      } else {
        setQuestions([]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [modalContentId, isModalOpen, activeSubject]);

  // フィードバック表示の制御
  useEffect(() => {
    if (Object.keys(sessionResponses).length > 0) {
      if (answerRevealMode === 'immediate') {
        setShowFeedback(true);
      }
    }
  }, [sessionResponses, answerRevealMode]);

  // クイズ開始時の初期化
  const startQuiz = useCallback(() => {
    setSessionResponses({});
    setShowFeedback(false);
    setShowFinalSummary(false);
    setIsFinalizingAttempt(false);
    attemptFinalizedRef.current = false;
    setStartTime(Date.now());
    setEndTime(null);
  }, []);

  // クイズリセット
  const resetQuiz = useCallback(() => {
    setSessionResponses({});
    setShowFeedback(false);
    setShowFinalSummary(false);
    setIsFinalizingAttempt(false);
    attemptFinalizedRef.current = false;
    setStartTime(null);
    setEndTime(null);
  }, []);

  // startTimeのみ設定（他の状態をリセットしない）
  const setStartTimeOnly = useCallback(() => {
    if (!startTime) {
      setStartTime(Date.now());
    }
  }, [startTime]);

  // 次の問題に進む処理（タイマー制御はQuizSectionで実行）
  const proceedToNext = useCallback(() => {
    // タイマー制御はQuizSectionで一元管理されるため、ここでは実行しない
    logger.info('proceedToNext - タイマー制御はQuizSectionで実行');
    setShowFeedback(false);
  }, []);

  // 強制的にshowFinalSummaryを設定する関数
  const forceShowFinalSummary = useCallback(() => {
    logger.debug('強制的にshowFinalSummaryをtrueに設定');
    setShowFinalSummary(true);
    attemptFinalizedRef.current = true;
  }, []);

  return {
    // 状態
    questions,
    sessionResponses,
    showFeedback,
    showFinalSummary,
    freeNavigation,
    answerRevealMode,
    isFinalizingAttempt,
    startTime,
    endTime,
    attemptFinalizedRef,

    // アクション
    handleQuestionSubmit,
    finalizeAttempt,
    startQuiz,
    resetQuiz,
    setShowFeedback,
    setStartTimeOnly,
    proceedToNext,
    forceShowFinalSummary,
    scrollToTop
  };
};

export default useQuizPlayer; 